// Derives the three-gate progress shape for a milestone from its four persisted
// lifecycle timestamps and plannedDate. Mirrors backend deriveProgressGates.ts;
// any change here must be applied to both files simultaneously.

import { getBrisbaneToday } from "./getBrisbaneToday";
import { isSet, type MilestoneInput } from "./milestoneInput";

export type DeliveredGate = "not_due_yet" | "on_time" | "delivered_late" | "late_undelivered";
export type AcceptedGate = "not_yet" | "partial" | "fully_accepted";
export type PaidGate = "not_yet" | "paid";

export interface ProgressGates {
  delivered: DeliveredGate;
  accepted: AcceptedGate;
  paid: PaidGate;
}


function deriveDeliveredGate(m: MilestoneInput, today: string): DeliveredGate {
  if (isSet(m.deliveredAt)) {
    if (isSet(m.plannedDate) && m.deliveredAt > m.plannedDate) {
      return "delivered_late";
    }
    return "on_time";
  }
  if (isSet(m.plannedDate) && m.plannedDate < today) {
    return "late_undelivered";
  }
  return "not_due_yet";
}

function deriveAcceptedGate(m: MilestoneInput): AcceptedGate {
  if (isSet(m.acceptedAt)) return "fully_accepted";
  if (isSet(m.partiallyAcceptedAt)) return "partial";
  return "not_yet";
}

function derivePaidGate(m: MilestoneInput): PaidGate {
  return isSet(m.paidAt) ? "paid" : "not_yet";
}

/**
 * Returns a three-gate progress shape read directly from the four lifecycle
 * timestamps and plannedDate. Each gate is independent; callers render them
 * individually (e.g. stepper, summary strip, gate chips).
 *
 * `today` defaults to Brisbane today; tests inject a stable value.
 */
export function deriveProgressGates(
  m: MilestoneInput,
  today: string = getBrisbaneToday(),
): ProgressGates {
  return {
    delivered: deriveDeliveredGate(m, today),
    accepted: deriveAcceptedGate(m),
    paid: derivePaidGate(m),
  };
}
